import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ProviderFeatureDiscoveryKey,
  ProviderFeatureDiscoveryStatusResponse,
} from "../../recoil/types";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import {
  GetProviderFeatureDiscoveryStatus,
  MarkProviderFeatureDiscoverySeen,
} from "./Services/FeatureDiscoveryService";
import { featureDiscoveryAutoOpenTours } from "./tourRegistry";
import {
  ActiveFeatureDiscoveryTour,
  FeatureDiscoveryContextValue,
  FeatureDiscoveryTooltipStorageKey,
  FeatureDiscoveryTourSource,
} from "./types";

const getTooltipStorageKey = (
  username: string,
  featureKey: ProviderFeatureDiscoveryKey,
): FeatureDiscoveryTooltipStorageKey =>
  `providerFeatureDiscoveryTooltipShown:${username}:${featureKey}`;

const defaultContext: FeatureDiscoveryContextValue = {
  activeTour: null,
  helpAnchor: null,
  prematureExitTooltipFeatureKey: null,
  isTourAvailable: () => false,
  openTour: () => undefined,
  closeTour: () => undefined,
  registerHelpAnchor: () => undefined,
  dismissPrematureExitTooltip: () => undefined,
};

const FeatureDiscoveryContext =
  createContext<FeatureDiscoveryContextValue>(defaultContext);

export const useFeatureDiscovery = (): FeatureDiscoveryContextValue =>
  useContext(FeatureDiscoveryContext);

export const FeatureDiscoveryProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const [status, setStatus] =
    useState<ProviderFeatureDiscoveryStatusResponse | null>(null);
  const [activeTour, setActiveTour] =
    useState<ActiveFeatureDiscoveryTour | null>(null);
  const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null);
  const [prematureExitTooltipFeatureKey, setPrematureExitTooltipFeatureKey] =
    useState<ProviderFeatureDiscoveryKey | null>(null);
  const hasFetchedRef = useRef(false);
  const hasAutoOpenedRef = useRef(false);
  const seenPostFeatureKeysRef = useRef<Set<ProviderFeatureDiscoveryKey>>(
    new Set(),
  );
  const authToken = auth?.token;
  const username = auth?.username ?? auth?.email ?? "unknown";

  useEffect(() => {
    hasFetchedRef.current = false;
    hasAutoOpenedRef.current = false;
    seenPostFeatureKeysRef.current = new Set();
    setStatus(null);
    setActiveTour(null);
    setPrematureExitTooltipFeatureKey(null);
  }, [authToken]);

  useEffect(() => {
    if (!authToken || hasFetchedRef.current) return;
    let isSubscribed = true;
    hasFetchedRef.current = true;

    GetProviderFeatureDiscoveryStatus(authToken, setLoggedOut)
      .then((featureDiscoveryStatus) => {
        if (isSubscribed) setStatus(featureDiscoveryStatus);
      })
      .catch(() => {
        if (isSubscribed) setStatus(null);
      });

    return () => {
      isSubscribed = false;
    };
  }, [authToken, setLoggedOut]);

  const isTourAvailable = useCallback(
    (featureKey: ProviderFeatureDiscoveryKey) =>
      Boolean(status?.eligible_feature_discoveries.includes(featureKey)),
    [status],
  );

  const openTour = useCallback(
    (
      featureKey: ProviderFeatureDiscoveryKey,
      source: FeatureDiscoveryTourSource,
    ) => {
      if (!isTourAvailable(featureKey)) return;
      setActiveTour({ featureKey, source });

      const hasBeenSeen = Boolean(
        status?.seen_feature_discoveries.some(
          (seenFeature) => seenFeature.feature_discovery === featureKey,
        ),
      );

      if (
        !authToken ||
        hasBeenSeen ||
        seenPostFeatureKeysRef.current.has(featureKey)
      ) {
        return;
      }

      seenPostFeatureKeysRef.current.add(featureKey);
      MarkProviderFeatureDiscoverySeen(authToken, featureKey, setLoggedOut)
        .then(() => undefined)
        .catch(() => undefined);
    },
    [
      authToken,
      isTourAvailable,
      setLoggedOut,
      status?.seen_feature_discoveries,
    ],
  );

  useEffect(() => {
    if (!status || hasAutoOpenedRef.current || activeTour) return;

    const firstUnseenTour = featureDiscoveryAutoOpenTours.find((tour) => {
      const isSeen = status.seen_feature_discoveries.some(
        (seenFeature) => seenFeature.feature_discovery === tour.featureKey,
      );
      return isTourAvailable(tour.featureKey) && !isSeen;
    });

    if (!firstUnseenTour) return;

    hasAutoOpenedRef.current = true;
    openTour(firstUnseenTour.featureKey, "auto");
  }, [activeTour, isTourAvailable, openTour, status]);

  const markTooltipShown = useCallback(
    (featureKey: ProviderFeatureDiscoveryKey) => {
      localStorage.setItem(getTooltipStorageKey(username, featureKey), "true");
      setPrematureExitTooltipFeatureKey(featureKey);
    },
    [username],
  );

  const closeTour = useCallback(
    (completed: boolean) => {
      if (
        activeTour?.source === "auto" &&
        !completed &&
        localStorage.getItem(
          getTooltipStorageKey(username, activeTour.featureKey),
        ) !== "true"
      ) {
        markTooltipShown(activeTour.featureKey);
      }
      setActiveTour(null);
    },
    [activeTour, markTooltipShown, username],
  );

  const dismissPrematureExitTooltip = useCallback(() => {
    setPrematureExitTooltipFeatureKey(null);
  }, []);

  const registerHelpAnchor = useCallback((element: HTMLElement | null) => {
    setHelpAnchor(element);
  }, []);

  const value = useMemo(
    () => ({
      activeTour,
      helpAnchor,
      prematureExitTooltipFeatureKey,
      isTourAvailable,
      openTour,
      closeTour,
      registerHelpAnchor,
      dismissPrematureExitTooltip,
    }),
    [
      activeTour,
      closeTour,
      dismissPrematureExitTooltip,
      helpAnchor,
      isTourAvailable,
      openTour,
      prematureExitTooltipFeatureKey,
      registerHelpAnchor,
    ],
  );

  return (
    <FeatureDiscoveryContext.Provider value={value}>
      {children}
    </FeatureDiscoveryContext.Provider>
  );
};
